import { useCallback, useEffect, useMemo, useState } from 'react';
import SiteHeader from '../components/SiteHeader';
import OpeningCard from '../components/OpeningCard';
import DeepenControl from '../components/DeepenControl';
import { studyApi, type AuthArgs } from '../api/studyApi';
import { useAuth } from '../auth/AuthContext';
import type { Pathway, PathStep } from '../../../shared/types';

/**
 * The whole suggested route through the catalog, start to finish.
 *
 * Every step is an opening *and a level*, because the question this page has to
 * answer is not only "what should I learn next" but "when do I stop collecting
 * openings and go back to make one of them deeper". A list of openings can only
 * answer the first.
 *
 * The order mixes the two deliberately: you steer most of your games into the
 * same few structures, so the next rung of the opening you play every game is
 * usually worth more than a first look at one you have avoided for months.
 * Each step says what share of your games it covers, so the ordering argues for
 * itself rather than asking to be trusted.
 *
 * Nothing is locked. The order is a suggestion: someone who wants the Najdorf
 * on day one should have it, and a path that refuses is one people work around
 * rather than follow.
 */
export default function PathwayPage() {
  const { getToken, refresh } = useAuth();
  const auth = useMemo(() => ({ getToken, refresh }), [getToken, refresh]);

  const [path, setPath] = useState<Pathway | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(
    () =>
      studyApi
        .pathway(auth)
        .then(setPath)
        .catch((e) => setError(e instanceof Error ? e.message : 'Could not load your pathway')),
    [auth],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <SiteHeader />
      <main className="page page--wide">
        <header className="section-head">
          <div>
            <h1>Your pathway</h1>
            <p className="muted">
              Built around the moves you actually play. You steer most games into the same few
              structures, so the path goes deeper into those before it goes wider — nothing is
              locked, so take anything out of order if you want to.
            </p>
          </div>
          {path && (
            <div className="path-progress">
              <strong>{path.doneCount}</strong> of {path.totalCount} done
            </div>
          )}
        </header>

        {error && <p className="error">{error}</p>}
        {!path && !error && <p className="muted">Loading…</p>}

        {/* The shape of the repertoire: one row per situation you need an
            answer for. This is the reasoning behind the order made visible —
            without it, "Italian · intermediate" sitting above "French ·
            beginner" looks arbitrary rather than like the whole point. */}
        {path && (
          <section className="path-shape">
            <h2 className="path-shape-title">
              Your repertoire · <code>1.{path.firstMove}</code> as White
            </h2>
            <ul className="path-slots">
              {[...path.slots]
                .sort((a, b) => b.reachPercent - a.reachPercent)
                .map((slot) => (
                  <li key={slot.key} className={slot.yours ? 'slot slot--yours' : 'slot'}>
                    <span className="slot-label">{slot.label}</span>
                    <span className="slot-fill">
                      {slot.openingName}
                      {slot.level && <span className="slot-level"> · {slot.level}</span>}
                      {!slot.yours && <span className="slot-suggested"> (suggested)</span>}
                    </span>
                    <span className="slot-reach">{slot.reachPercent.toFixed(0)}% of games</span>
                  </li>
                ))}
            </ul>
          </section>
        )}

        <ol className="path-steps">
          {path?.steps.map((step) => (
            <li key={`${step.openingId}:${step.level}`}>
              <StepCard step={step} auth={auth} onChanged={load} />
            </li>
          ))}
        </ol>
      </main>
    </>
  );
}

/**
 * One step: an opening at a level, with the reason it sits where it does.
 *
 * The "go deeper" control appears only on the rung your repertoire is actually
 * at — a button on a step three rungs above where you are would move the level
 * somewhere you did not ask for.
 */
function StepCard({
  step,
  auth,
  onChanged,
}: {
  step: PathStep;
  auth: AuthArgs;
  onChanged: () => void | Promise<void>;
}) {
  return (
    <OpeningCard
      opening={{ ...step, id: step.openingId }}
      eyebrow={
        <>
          Step {step.number} · {step.slotLabel}
        </>
      }
      highlight={step.status === 'current'}
      muted={step.status === 'done'}
      badge={
        <>
          <span className={`step-kind step-kind--${step.kind}`}>
            {step.kind === 'deepen' ? 'Go deeper' : 'New opening'} · <strong>{step.level}</strong>
          </span>
          {step.status === 'done' && <span className="path-badge path-badge--mastered">Done</span>}
          {step.status === 'current' && <span className="path-badge path-badge--next">Next up</span>}
        </>
      }
      footer={
        <div className="path-depth">
          <span className="path-depth-level">
            +{step.linesAdded} {step.linesAdded === 1 ? 'variation' : 'variations'} · comes up in
            about <strong>{step.reachPercent.toFixed(0)}%</strong> of your games
          </span>
          {step.depth && (
            <DeepenControl
              openingId={step.openingId}
              name={step.name}
              depth={step.depth}
              auth={auth}
              onChanged={onChanged}
              compact
            />
          )}
        </div>
      }
    />
  );
}
